import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import React from 'react';

import { Box, HStack, Icon, Skeleton, Text } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import {
  CardTypes,
  ICredit,
  IPaymentMethod,
  IPaymentState,
  IRevaultInformation,
} from 'features/payment/types';
import { mapPaymentMethodCardTypes, shouldReVault } from 'features/payment/utils';
import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import CashAppMethod from 'components/payment-method-option/cash-app-method';
import CashMethod from 'components/payment-method-option/cash-method';
import PayPalMethod from 'components/payment-method-option/paypal-method';
import PrepaidMethodBalance from 'components/payment-method-option/prepaid-method-balance';
import VenmoMethod from 'components/payment-method-option/venmo-method';
import { classifyPaymentMethods } from 'components/payment-method/utils';
import { theme } from 'styles/configure-theme';

import PaymentReloadRedesign from '../payment-reload-redesign/payment-reload-redesign';

import { PaymentIcon } from './payment-icon';
import { HorizontalContainer, TopContainer } from './styled';

export interface IPaymentRedesign {
  methods: IPaymentMethod[];
  selectedMethodId: string;
  reloadAmount: number;
  reloadPaymentValues: IPaymentState;
  setReloadPaymentValues: Dispatch<SetStateAction<IPaymentState>>;
  isDelivery: boolean;
  showReloadPaymentMethods: boolean;
  onChange?: () => void;
  onChangeReload?: () => void;
}

export const PaymentRedesign = ({
  methods,
  selectedMethodId,
  reloadAmount,
  reloadPaymentValues,
  setReloadPaymentValues,
  isDelivery,
  showReloadPaymentMethods,
  onChange,
  onChangeReload,
}: IPaymentRedesign) => {
  const { formatMessage } = useIntl();
  const {
    isOrbital: isOrbitalPaymentEnabled,
    loading: loadingPaymentMethods,
    savingPaymentMethods,
    noPaymentMethodsAvailable,
    setPaymentMethodByNativePriority,
  } = usePaymentContext();

  const { selectedMethod } = classifyPaymentMethods(methods, selectedMethodId);
  const revaultInformation: IRevaultInformation = shouldReVault({
    accountIdentifier: selectedMethodId || '',
    isOrbitalPaymentEnabled,
    methods,
  });
  const { isInvalid = false, isExpired = false } = revaultInformation ?? {};
  const isRevaultRequired = isInvalid || isExpired;

  useEffect(() => {
    if (isRevaultRequired) {
      setPaymentMethodByNativePriority();
    }
  }, [isRevaultRequired, setPaymentMethodByNativePriority]);

  const textColor = theme.token('text-default');
  const formatExpirationDate = useCallback(
    (credit: ICredit) => {
      return `${formatMessage({ id: 'expire' })} ${credit.expiryMonth}/${credit.expiryYear}`;
    },
    [formatMessage]
  );

  const shouldShowSkeleton =
    loadingPaymentMethods ||
    savingPaymentMethods ||
    (!noPaymentMethodsAvailable && !methods.length);

  const RenderMethodType = useCallback(() => {
    if (selectedMethod?.credit) {
      const creditCardAlias = (
        <Text color={textColor}>
          {formatMessage({ id: 'endsInRedesign' }).toLowerCase()}
          {selectedMethod?.credit.alias}
        </Text>
      );
      let isCreditCard;
      let alias;
      let cardType;
      switch (selectedMethod?.credit.cardType) {
        case CardTypes.APPLE_PAY:
          isCreditCard = false;
          alias = formatMessage({ id: 'applePay' });
          break;
        case CardTypes.GOOGLE_PAY:
          isCreditCard = false;
          alias = formatMessage({ id: 'googlePay' });
          break;
        default:
          cardType = mapPaymentMethodCardTypes(selectedMethod?.credit.cardType);
          isCreditCard = true;
          alias = creditCardAlias;
      }
      return (
        <>
          <HorizontalContainer>
            <PaymentIcon method={selectedMethod} isLargeSize={true} />
            {(isCreditCard && (
              <Box alignItems="flex-start" flexDirection="column">
                <Box flexDirection="row">
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color={textColor}
                    testID="payment-method-card"
                  >
                    {cardType || formatMessage({ id: 'card' })}
                  </Text>
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    ml="$1"
                    color={textColor}
                    textTransform="capitalize"
                  >
                    {alias}
                  </Text>
                </Box>
                <Text margin={0} variant="copyOne" textTransform="capitalize">
                  {formatExpirationDate(selectedMethod?.credit)}
                </Text>
              </Box>
            )) || (
              <Text
                variant="headerTwo"
                fontSize="lg"
                mt="$2"
                color={textColor}
                textTransform="capitalize"
              >
                {alias}
              </Text>
            )}
          </HorizontalContainer>
        </>
      );
    }

    if (selectedMethod?.cash) {
      return (
        <HorizontalContainer>
          <PaymentIcon method={selectedMethod} isMediumSize={true} />
          <CashMethod isRedesign />
        </HorizontalContainer>
      );
    }

    if (selectedMethod?.prepaid) {
      return (
        <HorizontalContainer>
          <PaymentIcon method={selectedMethod} isLargeSize={true} />
          <PrepaidMethodBalance method={selectedMethod} isRedesign />
        </HorizontalContainer>
      );
    }

    if (selectedMethod?.paypal) {
      return (
        <HorizontalContainer>
          <PaymentIcon method={selectedMethod} isLargeSize={true} />
          <PayPalMethod isRedesign />
        </HorizontalContainer>
      );
    }

    if (selectedMethod?.venmo) {
      return (
        <HorizontalContainer>
          <PaymentIcon method={selectedMethod} isLargeSize={true} />
          <VenmoMethod isRedesign />
        </HorizontalContainer>
      );
    }

    if (selectedMethod?.cashapp) {
      return (
        <HorizontalContainer>
          <PaymentIcon method={selectedMethod} isLargeSize={true} />
          <CashAppMethod isRedesign />
        </HorizontalContainer>
      );
    }
    return null;
  }, [formatExpirationDate, formatMessage, selectedMethod, textColor]);

  return (
    <>
      <TopContainer>
        {shouldShowSkeleton ? (
          <>
            <HorizontalContainer>
              <Skeleton height="27px" width="40px" rounded="xs" />
              <Skeleton.Text
                lines={2}
                _line={{
                  height: '12px',
                }}
                space="$2"
                width="150px"
              />
            </HorizontalContainer>
            <Skeleton
              width="72px"
              height="30px"
              rounded="2xl"
              marginY="$6"
              marginRight="$3"
              marginLeft={{ base: '$1', md: '$6' }}
            />
          </>
        ) : (
          <>
            {noPaymentMethodsAvailable ? (
              <HStack width="full" justifyContent="space-between" paddingX="$4">
                <ActionButton
                  testID="add-payment-btn"
                  width="100%"
                  height="$10"
                  marginY="$6"
                  _text={{ fontSize: 'sm' }}
                  variant={ActionButtonVariants.OUTLINE}
                  leftIcon={<Icon variant="add" aria-hidden />}
                  onPress={onChange}
                >
                  {formatMessage({ id: 'addPayment' })}
                </ActionButton>
              </HStack>
            ) : (
              <>
                <RenderMethodType />
                <ActionButton
                  testID="change-btn"
                  width="auto"
                  height="$10"
                  marginY="$6"
                  marginRight="$3"
                  marginLeft="auto"
                  _text={{ fontSize: 'sm' }}
                  variant={ActionButtonVariants.OUTLINE}
                  onPress={onChange}
                >
                  {formatMessage({ id: 'change' })}
                </ActionButton>
              </>
            )}
          </>
        )}
      </TopContainer>
      {showReloadPaymentMethods && !shouldShowSkeleton && (
        <PaymentReloadRedesign
          reloadAmount={reloadAmount}
          reloadPaymentValues={reloadPaymentValues}
          setReloadPaymentValues={setReloadPaymentValues}
          isDelivery={isDelivery}
          methods={methods}
          selectedMethodId={selectedMethodId}
          showReloadPaymentMethods={false}
          onChange={onChangeReload}
        />
      )}
    </>
  );
};
